import { cx } from '../ui/cx.ts';
import { ChartXAxis } from './ChartXAxis.tsx';
import type { ChartFillClass, ChartStrokeClass } from './ramp.ts';

/**
 * Compact SVG trend line — Field Notes chart set.
 *
 * Dependency-free: geometry is computed inline and every color comes
 * from a Field Notes utility class (`stroke-*` / `fill-*` generated by
 * `tailwind.config.ts` off `src/ui/tokens.ts`) — never hex, never a
 * chart library.
 *
 * `strokeClassName` / `dotClassName` are closed unions over the fixed
 * chart ramp of DESIGN.md §2 (forest → moss → rust → warn →
 * line-strong), same discipline as `Sparkline`: a caller cannot drift a
 * chart off-token. The unions are DERIVED from `ramp.ts`, the same
 * literals `StackedBars` cycles, so the two chart types cannot end up
 * with different ideas of the ramp. A class name that is merely
 * well-FORMED but absent from the palette produces no error and no
 * style, which is exactly the failure a closed union removes.
 *
 * Accessibility: the svg is exposed as ONE image (`role="img"` with a
 * mandatory `ariaLabel`, mirrored into `<title>`); the geometry inside
 * is decoration. Dots carry per-point `<title>` tooltips from `labels`
 * for pointer users. A chart must never be the sole carrier of a
 * number — consumers surface the key values as text alongside
 * (DESIGN.md §9).
 *
 * Incomplete points (a bucket the window only partly covers — on the
 * dashboard, "today") are marked THREE ways: the segment reaching them
 * is dashed, their dot is faded, and `incompleteLabel` is appended to
 * their tooltip. Dash pattern and opacity are SVG attributes, not
 * colors, so the fixed ramp is untouched; the text half is mandatory
 * because DESIGN.md §9 forbids information carried by fade alone.
 *
 * Point-math edges: an empty series draws the baseline grid only; a
 * single point renders one centered dot (no polyline); an all-zero
 * series draws a flat line on the baseline (the max is floored at 1 so
 * there is no division by zero); a negative value is clamped to the
 * baseline rather than drawn below the axis, since this chart has no
 * negative half — an unclamped point would escape the viewBox.
 */

const VIEW_WIDTH = 640;
const PADDING_X = 12;
const PADDING_Y = 10;
const INCOMPLETE_DASH = '5 4';
const INCOMPLETE_DOT_OPACITY = 0.4;

interface Props {
  readonly points: readonly number[];
  /** Per-point tooltip label; falls back to the numeric value. */
  readonly labels?: readonly string[];
  /**
   * Aligned by index with `points`: true where the bucket behind the
   * point is only partly covered. Omitted means "every point is whole".
   */
  readonly incomplete?: readonly boolean[];
  /** Display-ready marker appended to an incomplete point's tooltip. */
  readonly incompleteLabel?: string;
  /**
   * Per-point x-axis tick text. Omitted draws no axis row — a sparkline
   * inside a table cell has no room for one.
   */
  readonly tickLabels?: readonly string[];
  readonly strokeClassName?: ChartStrokeClass;
  readonly dotClassName?: ChartFillClass;
  /** Rendered pixel height (doubles as the viewBox height). */
  readonly height?: number;
  readonly dots?: boolean;
  readonly ariaLabel: string;
  readonly testId?: string;
}

export function Line({
  points,
  labels,
  incomplete,
  incompleteLabel,
  tickLabels,
  strokeClassName = 'stroke-forest',
  dotClassName = 'fill-forest',
  height = 96,
  dots = true,
  ariaLabel,
  testId,
}: Props): JSX.Element {
  const max = Math.max(1, ...points);
  const drawableWidth = VIEW_WIDTH - PADDING_X * 2;
  const drawableHeight = height - PADDING_Y * 2;
  const coordinates = points.map((value, index) => {
    const x =
      points.length <= 1
        ? VIEW_WIDTH / 2
        : PADDING_X + (index / (points.length - 1)) * drawableWidth;
    const y = PADDING_Y + drawableHeight - (Math.max(0, value) / max) * drawableHeight;
    return { x, y };
  });

  // The incomplete run is always a SUFFIX in practice (the trailing
  // in-progress bucket), so one dashed polyline from the last whole
  // point onwards covers it; the solid one stops there. Overlapping by
  // one point keeps the two joined rather than leaving a visual gap.
  const firstIncomplete = incomplete?.findIndex((flag) => flag) ?? -1;
  const splitAt = firstIncomplete >= 0 ? firstIncomplete : coordinates.length;
  const solid = coordinates.slice(0, splitAt);
  const dashed = splitAt < coordinates.length ? coordinates.slice(Math.max(0, splitAt - 1)) : [];

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="none"
        style={{ height }}
        className="block w-full"
      >
        <title>{ariaLabel}</title>
        {[0, 1, 2].map((line) => {
          const y = PADDING_Y + (line / 2) * drawableHeight;
          return (
            <line
              key={line}
              x1={PADDING_X}
              x2={VIEW_WIDTH - PADDING_X}
              y1={y}
              y2={y}
              className="stroke-line-soft"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {solid.length > 1 ? (
          <polyline
            points={solid.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')}
            className={cx('fill-none', strokeClassName)}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        {dashed.length > 1 ? (
          <polyline
            points={dashed.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')}
            className={cx('fill-none', strokeClassName)}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={INCOMPLETE_DASH}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        {dots
          ? coordinates.map((c, index) => {
              const partial = incomplete?.[index] === true;
              const label = labels?.[index] ?? String(points[index] ?? 0);
              return (
                <circle
                  key={index}
                  cx={c.x.toFixed(1)}
                  cy={c.y.toFixed(1)}
                  r={3}
                  className={dotClassName}
                  fillOpacity={partial ? INCOMPLETE_DOT_OPACITY : undefined}
                >
                  <title>
                    {partial && incompleteLabel !== undefined
                      ? `${label} · ${incompleteLabel}`
                      : label}
                  </title>
                </circle>
              );
            })
          : null}
      </svg>
      {tickLabels === undefined ? null : (
        <ChartXAxis
          labels={tickLabels}
          layout="points"
          testId={testId === undefined ? undefined : `${testId}-ticks`}
        />
      )}
    </div>
  );
}
